import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, apiErr, track } from "../lib/api";
import { Button } from "./primitives";

const BUSINESS_TYPES = ["Importer", "Distributor", "Wholesaler", "Retailer", "E-commerce", "Supermarket / Retail Chain", "Health & Wellness Store", "Trading Company", "Other"];
const VOLUMES = ["Trial Order", "Small Wholesale", "Regional Distribution", "National Distribution", "Strategic / Large Opportunity"];
const PRODUCTS = ["Moringa Powder", "Hibiscus Powder", "Beetroot Powder", "Amla Powder", "Turmeric Powder", "Makhana", "Future Products"];
const PARTNERSHIP = ["Import", "Distribution", "Wholesale", "Retail", "E-commerce", "Bulk Supply", "Private Label", "Other"];

const Field = ({ label, req, children }) => (
  <label className="block">
    <span className="text-sm text-botanical/80 font-medium">{label}{req && <span className="text-hibiscus"> *</span>}</span>
    <div className="mt-2">{children}</div>
  </label>
);
const inputCls = "w-full bg-transparent border-b border-border focus:border-botanical outline-none py-2.5 text-botanical placeholder:text-muted-foreground/50 transition-colors";

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`text-sm px-4 py-2 rounded-full border transition-all ${active ? "bg-botanical text-ivory border-botanical" : "border-border text-botanical/70 hover:border-botanical/50"}`}>
    {children}
  </button>
);

export default function EnquiryForm({ enquiryType = "b2b", multiStep = true }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    enquiry_type: enquiryType, full_name: "", company: "", email: "", phone: "", country: "", city: "",
    website: "", business_type: "", years_in_business: "", num_locations: "", current_categories: "",
    target_country: "", target_region: "", markets_served: "", distribution_network: "", estimated_volume: "",
    products: [], partnership_types: [], pack_size: "", quantity: "", frequency: "", destination: "",
    certifications: "", message: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const steps = multiStep
    ? ["Contact", "Business", "Market", "Products", "Partnership", "Message"]
    : ["Details"];

  const canNext = () => {
    if (!multiStep) return f.full_name && f.email;
    if (step === 0) return f.full_name && f.company && f.email && f.phone && f.country;
    if (step === 1) return f.business_type;
    if (step === 2) return f.target_country;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", f);
      track(`${enquiryType}_enquiry`, { country: f.target_country || f.country });
      setDone(true);
      toast.success("Enquiry submitted — we'll be in touch shortly.");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="border border-border rounded-md bg-white p-12 text-center" data-testid="enquiry-success">
      <div className="mx-auto h-14 w-14 rounded-full bg-moringa/10 flex items-center justify-center"><Check className="h-7 w-7 text-moringa" /></div>
      <h3 className="font-serif text-4xl text-botanical mt-6">Thank You</h3>
      <p className="text-muted-foreground mt-3 max-w-md mx-auto">We've received your enquiry and sent a confirmation to your email. Our team will review your requirements and follow up shortly.</p>
    </div>
  );

  return (
    <div className="border border-border rounded-md bg-white p-8 md:p-12" data-testid="enquiry-form">
      {multiStep && (
        <div className="flex items-center gap-2 mb-10 flex-wrap">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`text-xs px-3 py-1.5 rounded-full transition-colors ${i === step ? "bg-botanical text-ivory" : i < step ? "text-moringa" : "text-muted-foreground"}`}>
                {i + 1}. {s}
              </span>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.35 }}>
          {(!multiStep || step === 0) && (
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Full Name" req><input className={inputCls} data-testid="ef-name" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} /></Field>
              <Field label="Company Name" req={multiStep}><input className={inputCls} data-testid="ef-company" value={f.company} onChange={(e) => set("company", e.target.value)} /></Field>
              <Field label="Business Email" req><input type="email" className={inputCls} data-testid="ef-email" value={f.email} onChange={(e) => set("email", e.target.value)} /></Field>
              <Field label="Phone / WhatsApp" req={multiStep}><input className={inputCls} data-testid="ef-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
              <Field label="Country" req={multiStep}><input className={inputCls} data-testid="ef-country" value={f.country} onChange={(e) => set("country", e.target.value)} /></Field>
              {multiStep && <><Field label="City"><input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} /></Field>
              <Field label="Company Website"><input className={inputCls} value={f.website} onChange={(e) => set("website", e.target.value)} /></Field></>}
              {!multiStep && enquiryType === "export" && <>
                <Field label="Product"><input className={inputCls} value={f.products[0] || ""} onChange={(e) => set("products", [e.target.value])} /></Field>
                <Field label="Pack Size"><input className={inputCls} value={f.pack_size} onChange={(e) => set("pack_size", e.target.value)} /></Field>
                <Field label="Quantity"><input className={inputCls} value={f.quantity} onChange={(e) => set("quantity", e.target.value)} /></Field>
                <Field label="Expected Frequency"><input className={inputCls} value={f.frequency} onChange={(e) => set("frequency", e.target.value)} /></Field>
                <Field label="Destination Market"><input className={inputCls} value={f.destination} onChange={(e) => set("destination", e.target.value)} /></Field>
                <Field label="Required Certifications"><input className={inputCls} value={f.certifications} onChange={(e) => set("certifications", e.target.value)} /></Field>
              </>}
              {!multiStep && <div className="md:col-span-2"><Field label="Message"><textarea rows={4} className={inputCls} data-testid="ef-message" value={f.message} onChange={(e) => set("message", e.target.value)} /></Field></div>}
            </div>
          )}
          {multiStep && step === 1 && (
            <div className="space-y-8">
              <Field label="Business Type" req><div className="flex flex-wrap gap-2">{BUSINESS_TYPES.map((b) => <Chip key={b} active={f.business_type === b} onClick={() => set("business_type", b)} testId={`ef-bt-${b}`}>{b}</Chip>)}</div></Field>
              <div className="grid md:grid-cols-3 gap-6">
                <Field label="Years in Business"><input className={inputCls} value={f.years_in_business} onChange={(e) => set("years_in_business", e.target.value)} /></Field>
                <Field label="Number of Locations"><input className={inputCls} value={f.num_locations} onChange={(e) => set("num_locations", e.target.value)} /></Field>
                <Field label="Current Product Categories"><input className={inputCls} value={f.current_categories} onChange={(e) => set("current_categories", e.target.value)} /></Field>
              </div>
            </div>
          )}
          {multiStep && step === 2 && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Target Country" req><input className={inputCls} data-testid="ef-target-country" value={f.target_country} onChange={(e) => set("target_country", e.target.value)} /></Field>
                <Field label="Target Region"><input className={inputCls} value={f.target_region} onChange={(e) => set("target_region", e.target.value)} /></Field>
                <Field label="Markets Currently Served"><input className={inputCls} value={f.markets_served} onChange={(e) => set("markets_served", e.target.value)} /></Field>
                <Field label="Existing Distribution Network"><input className={inputCls} value={f.distribution_network} onChange={(e) => set("distribution_network", e.target.value)} /></Field>
              </div>
              <Field label="Estimated Purchase Potential"><div className="flex flex-wrap gap-2">{VOLUMES.map((v) => <Chip key={v} active={f.estimated_volume === v} onClick={() => set("estimated_volume", v)}>{v}</Chip>)}</div></Field>
            </div>
          )}
          {multiStep && step === 3 && (
            <Field label="Product Interest"><div className="flex flex-wrap gap-2">{PRODUCTS.map((p) => <Chip key={p} active={f.products.includes(p)} onClick={() => toggle("products", p)} testId={`ef-prod-${p}`}>{p}</Chip>)}</div></Field>
          )}
          {multiStep && step === 4 && (
            <Field label="Partnership Type"><div className="flex flex-wrap gap-2">{PARTNERSHIP.map((p) => <Chip key={p} active={f.partnership_types.includes(p)} onClick={() => toggle("partnership_types", p)}>{p}</Chip>)}</div></Field>
          )}
          {multiStep && step === 5 && (
            <Field label="Tell us about your company, market and requirements"><textarea rows={6} className={inputCls} data-testid="ef-message" value={f.message} onChange={(e) => set("message", e.target.value)} /></Field>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between items-center mt-10 pt-8 border-t border-border">
        {multiStep && step > 0
          ? <button onClick={() => setStep(step - 1)} className="text-sm text-muted-foreground hover:text-botanical" data-testid="ef-back">← Back</button>
          : <span />}
        {multiStep && step < steps.length - 1
          ? <Button onClick={() => canNext() ? setStep(step + 1) : toast.error("Please complete the required fields.")} testId="ef-next">Continue</Button>
          : <Button onClick={() => canNext() ? submit() : toast.error("Please complete the required fields.")} testId="ef-submit">{submitting ? "Submitting…" : "Submit Partnership Enquiry"}</Button>}
      </div>
    </div>
  );
}
